import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, Plus } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, TBody, TD, TH, THead, TR, Table } from '@/components/ui/Table';
import { SectionSpinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/Toast';
import { banksApi } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';

export function BanksPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isOpen, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.banks.list(true),
    queryFn: () => banksApi.list(true),
  });

  const createBank = useMutation({
    mutationFn: () => banksApi.create({ name: name.trim(), shortCode: shortCode.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.banks.all });
      toast.success(t('bank.created'));
      setOpen(false);
      setName('');
      setShortCode('');
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      banksApi.update(id, { isActive }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.banks.all });
      toast.success(t('bank.updated'));
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('bank.title')}
        description={t('bank.shortCodeHelp')}
        actions={
          <Button onClick={() => setOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
            {t('bank.new')}
          </Button>
        }
      />

      <Card>
        {isLoading ? (
          <SectionSpinner />
        ) : !data || data.length === 0 ? (
          <EmptyState icon={<Landmark className="h-6 w-6" />} title={t('common.noResults')} />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('bank.name')}</TH>
                <TH align="center">{t('bank.shortCode')}</TH>
                <TH align="center">{t('common.status')}</TH>
                <TH align="right">{t('common.actions')}</TH>
              </TR>
            </THead>

            <TBody>
              {data.map((bank) => (
                <TR key={bank.id}>
                  <TD className="font-medium text-slate-900">{bank.name}</TD>
                  <TD align="center">
                    <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs">
                      {bank.shortCode}
                    </span>
                  </TD>
                  <TD align="center">
                    <Badge tone={bank.isActive ? 'success' : 'neutral'}>
                      {bank.isActive ? t('common.active') : t('common.inactive')}
                    </Badge>
                  </TD>
                  <TD align="right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive.mutate({ id: bank.id, isActive: !bank.isActive })}
                    >
                      {bank.isActive ? t('common.inactive') : t('common.active')}
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Modal
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        title={t('bank.new')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => createBank.mutate()}
              isLoading={createBank.isPending}
              disabled={!name.trim() || !shortCode.trim()}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('bank.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            autoFocus
          />
          <Input
            label={t('bank.shortCode')}
            value={shortCode}
            onChange={(event) => setShortCode(event.target.value)}
            hint={t('bank.shortCodeHelp')}
            required
          />
        </div>
      </Modal>
    </div>
  );
}
